import * as THREE from 'three';
import {
  computeStormTickDamage,
  finalCircleCrunchMultiplier,
  isOutsideStorm,
  shrinkStormRadius,
} from './matchRules.js';

export type FpsStormOptions = {
  center?: THREE.Vector3;
  initialRadius?: number;
  shrinkPerSecond?: number;
  minRadius?: number;
  damagePerSecond?: number;
  /** Multiplied with `currentRadius` for `isOutside` / damage boundary (default 0.95). */
  outsideEdgeFactor?: number;
  /** Safe-zone radius (m) below which storm damage doubles — final circle crunch (default 28). */
  finalCircleSafeRadius?: number;
  /** Wall thickness (meters) beyond `currentRadius` for the visible ring (default 14). */
  wallDepth?: number;
  /** Optional scene fog to tint when the player is outside the safe zone. */
  fog?: THREE.Fog;
};

export class FpsStorm {
  private readonly scene: THREE.Scene;
  private center: THREE.Vector3;
  private readonly initialRadius: number;
  private shrinkPerSecond: number;
  private stormMinRadius: number;
  private damagePerSecond: number;
  private damageMultiplier = 1;
  private readonly finalCircleSafeRadius: number;
  private finalCircleCrunchMult = 1;
  private outsideEdgeFactor: number;
  private wallDepth: number;

  private currentRadius: number;
  private nextCenter: THREE.Vector3;
  private nextRadius: number;
  private stormPhase = 1;
  private readonly mesh: THREE.Mesh<THREE.RingGeometry, THREE.MeshBasicMaterial>;
  private readonly rimMesh: THREE.Mesh<THREE.RingGeometry, THREE.MeshBasicMaterial>;
  private readonly wallMesh: THREE.Mesh<THREE.CylinderGeometry, THREE.MeshStandardMaterial>;
  private readonly lightningLines: THREE.LineSegments<THREE.BufferGeometry, THREE.LineBasicMaterial>;
  private readonly lightningPositions: Float32Array;
  private readonly lightningPosAttr: THREE.BufferAttribute;
  private readonly wallHeight = 52;
  private readonly lightningArcCount = 7;
  private readonly lightningSegmentsPerArc = 4;
  private readonly rimCyan = new THREE.Color(0x44eeff);
  private readonly rimPurple = new THREE.Color(0xbb55ff);
  private readonly rimScratch = new THREE.Color();
  private fog?: THREE.Fog;
  private readonly fogBase?: { color: THREE.Color; near: number; far: number };
  private outsideTintActive = false;
  private readonly tintColor = new THREE.Color(0x9b4dca);
  private readonly lerpScratch = new THREE.Color();
  private trackedPx = 0;
  private trackedPz = 0;
  private pulseT = 0;
  private lightningFlash = 0;
  private lightningCd = 1.4 + Math.random() * 2.8;
  private skyLightningFlash = 0;
  private colorblindMode = false;
  private readonly wallBaseColor = new THREE.Color(0x9b4dff);
  private readonly wallCbColor = new THREE.Color(0x22d4ff);
  private readonly flashWhite = new THREE.Color(0xffffff);
  private readonly outsideWallColor = new THREE.Color(0xdd88ff);
  private readonly outsideWallCbColor = new THREE.Color(0xffaa66);

  constructor(scene: THREE.Scene, options?: FpsStormOptions) {
    this.scene = scene;
    this.center = options?.center?.clone() ?? new THREE.Vector3(0, 0, 0);
    this.initialRadius = options?.initialRadius ?? 120;
    this.currentRadius = this.initialRadius;
    this.shrinkPerSecond = options?.shrinkPerSecond ?? 2.5;
    this.stormMinRadius = options?.minRadius ?? 18;
    this.damagePerSecond = options?.damagePerSecond ?? 8;
    this.finalCircleSafeRadius = options?.finalCircleSafeRadius ?? 28;
    this.outsideEdgeFactor = options?.outsideEdgeFactor ?? 0.95;
    this.wallDepth = options?.wallDepth ?? 14;

    this.nextCenter = this.center.clone();
    this.nextRadius = this.currentRadius * this.outsideEdgeFactor;
    this.planNextZone();

    const mat = new THREE.MeshBasicMaterial({
      color: 0xc44cff,
      transparent: true,
      opacity: 0.42,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    const inner = Math.max(0.1, this.currentRadius - this.wallDepth * 0.35);
    const outer = this.currentRadius + this.wallDepth;
    const geo = new THREE.RingGeometry(inner, outer, 96);

    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.rotation.x = -Math.PI / 2;
    this.mesh.position.set(this.center.x, 0.08, this.center.z);
    this.mesh.renderOrder = 1;
    this.scene.add(this.mesh);

    const safeR = Math.max(0.5, this.getSafeZoneRadius());
    const rimInner = Math.max(0.1, safeR - 1.4);
    const rimOuter = safeR + 0.15;
    const rimGeo = new THREE.RingGeometry(rimInner, rimOuter, 96);
    const rimColors = new Float32Array(rimGeo.attributes.position.count * 3);
    const rimPos = rimGeo.attributes.position;
    for (let i = 0; i < rimPos.count; i++) {
      const x = rimPos.getX(i);
      const y = rimPos.getY(i);
      const t = (Math.atan2(y, x) / (Math.PI * 2) + 0.5) % 1;
      this.rimScratch.copy(this.rimCyan).lerp(this.rimPurple, t);
      rimColors[i * 3] = this.rimScratch.r;
      rimColors[i * 3 + 1] = this.rimScratch.g;
      rimColors[i * 3 + 2] = this.rimScratch.b;
    }
    rimGeo.setAttribute('color', new THREE.BufferAttribute(rimColors, 3));
    const rimMat = new THREE.MeshBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.62,
      side: THREE.DoubleSide,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.rimMesh = new THREE.Mesh(rimGeo, rimMat);
    this.rimMesh.rotation.x = -Math.PI / 2;
    this.rimMesh.position.set(this.center.x, 0.12, this.center.z);
    this.rimMesh.renderOrder = 3;
    this.scene.add(this.rimMesh);

    const wallMat = new THREE.MeshStandardMaterial({
      color: this.wallBaseColor.clone(),
      transparent: true,
      opacity: 0.32,
      side: THREE.DoubleSide,
      depthWrite: false,
      emissive: 0x000000,
      emissiveIntensity: 0,
      roughness: 0.35,
      metalness: 0.08,
    });
    const wallGeo = new THREE.CylinderGeometry(safeR, safeR, this.wallHeight, 96, 1, true);
    this.wallMesh = new THREE.Mesh(wallGeo, wallMat);
    this.wallMesh.position.set(this.center.x, this.wallHeight * 0.5, this.center.z);
    this.wallMesh.renderOrder = 2;
    this.scene.add(this.wallMesh);

    const lightningVertexCount = this.lightningArcCount * this.lightningSegmentsPerArc * 2;
    this.lightningPositions = new Float32Array(lightningVertexCount * 3);
    const lightningGeo = new THREE.BufferGeometry();
    this.lightningPosAttr = new THREE.BufferAttribute(this.lightningPositions, 3);
    lightningGeo.setAttribute('position', this.lightningPosAttr);
    const lightningMat = new THREE.LineBasicMaterial({
      color: 0xdff8ff,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.lightningLines = new THREE.LineSegments(lightningGeo, lightningMat);
    this.lightningLines.renderOrder = 4;
    this.scene.add(this.lightningLines);

    if (options?.fog) {
      this.fog = options.fog;
      this.fogBase = {
        color: options.fog.color.clone(),
        near: options.fog.near,
        far: options.fog.far,
      };
    }
  }

  get radius(): number {
    return this.currentRadius;
  }

  getMinRadius(): number {
    return this.stormMinRadius;
  }

  getShrinkPerSecond(): number {
    return this.shrinkPerSecond;
  }

  setShrinkPerSecond(rate: number): void {
    this.shrinkPerSecond = Math.max(0, rate);
  }

  setDamageMultiplier(mult: number): void {
    this.damageMultiplier = Math.max(0, mult);
  }

  /** Reposition storm center (and next-zone preview) — e.g. daily map rotation. */
  setCenter(x: number, z: number): void {
    this.center.set(x, this.center.y, z);
    this.nextCenter.copy(this.center);
    this.mesh.position.set(x, 0.08, z);
    this.rimMesh.position.set(x, 0.12, z);
    this.wallMesh.position.set(x, this.wallHeight * 0.5, z);
    this.lightningLines.position.set(x, 0, z);
    this.planNextZone();
  }

  /** True while the storm radius is still decreasing toward `minRadius`. */
  isActivelyShrinking(): boolean {
    return this.currentRadius > this.stormMinRadius + 0.08;
  }

  /** Seconds until the storm reaches minimum radius; null when already fully shrunk. */
  getSecondsUntilNextShrink(): number | null {
    if (!this.isActivelyShrinking()) return null;
    if (this.shrinkPerSecond <= 0) return null;
    return Math.max(0, (this.currentRadius - this.stormMinRadius) / this.shrinkPerSecond);
  }

  /** Seconds until the current phase shrink completes; null when not shrinking or at final phase. */
  getShrinkEtaSeconds(): number | null {
    if (!this.isActivelyShrinking()) return null;
    if (this.shrinkPerSecond <= 0) return null;
    const range = this.initialRadius - this.stormMinRadius;
    if (!Number.isFinite(range) || range <= 0) return null;
    const phase = this.phaseFromRadius();
    if (phase >= 6) return null;
    const band = range / 6;
    const nextBoundaryRadius = this.initialRadius - phase * band;
    const deltaR = this.currentRadius - nextBoundaryRadius;
    if (deltaR <= 0.05) return 0;
    return deltaR / this.shrinkPerSecond;
  }

  getStormPhase(): number {
    return this.stormPhase;
  }

  /** 0 at phase 1, 1 at phase 6 — drives closing-storm sky warmth. */
  getSkyWarmth01(): number {
    return (this.stormPhase - 1) / 5;
  }

  /** 0–1 flash intensity for sky lightning on phase advance (decays in update). */
  getSkyLightningFlash01(): number {
    return this.skyLightningFlash;
  }

  /** Safe zone radius (storm damage boundary) in world XZ. */
  getSafeZoneRadius(): number {
    return this.currentRadius * this.outsideEdgeFactor;
  }

  /** True when the safe zone has shrunk into final-circle crunch range. */
  isFinalCircle(): boolean {
    return this.getSafeZoneRadius() < this.finalCircleSafeRadius;
  }

  /** True during the final two storm phases (dramatic endgame). */
  isLateCircle(): boolean {
    return this.stormPhase >= 5 || this.isFinalCircle();
  }

  /** Active bot cap during late circles, or null when uncapped. */
  getActiveBotCap(): number | null {
    return this.isLateCircle() ? 3 : null;
  }

  /** Effective storm damage multiplier including final-circle crunch (2× when active). */
  getEffectiveDamageMultiplier(): number {
    this.finalCircleCrunchMult = finalCircleCrunchMultiplier(
      this.currentRadius,
      this.outsideEdgeFactor,
      this.finalCircleSafeRadius,
    );
    return this.damageMultiplier * this.finalCircleCrunchMult;
  }

  /** Late-game threshold — storm surge warning when alive players drop below 10. */
  shouldShowSurgeWarning(alivePlayers: number): boolean {
    return alivePlayers > 0 && alivePlayers < 10;
  }

  /** HUD label for storm surge damage warning. */
  getSurgeWarningText(alivePlayers: number): string {
    return `STORM SURGE — ${alivePlayers} alive · excess damage`;
  }

  /** Preview center of the next safe zone after the current phase completes. */
  getNextCenterXZ(): { x: number; z: number } {
    return { x: this.nextCenter.x, z: this.nextCenter.z };
  }

  /** Preview safe-zone radius at the end of the current storm phase. */
  getNextSafeZoneRadius(): number {
    return this.nextRadius;
  }

  getCenterXZ(): { x: number; z: number } {
    return { x: this.center.x, z: this.center.z };
  }

  /** Alternate storm palette for colorblind-friendly visibility. */
  setColorblindMode(enabled: boolean): void {
    this.colorblindMode = enabled;
    this.mesh.material.color.setHex(enabled ? 0xff8844 : 0xc44cff);
    this.tintColor.setHex(enabled ? 0xffaa55 : 0x9b4dca);
    this.wallMesh.material.color.copy(enabled ? this.wallCbColor : this.wallBaseColor);
    this.rimCyan.setHex(enabled ? 0xffcc66 : 0x44eeff);
    this.rimPurple.setHex(enabled ? 0xff8844 : 0xbb55ff);
    this.syncRimGeometry();
  }

  /** Storm has reached final circle size. */
  isFullyShrunk(): boolean {
    return this.currentRadius <= this.stormMinRadius + 0.08;
  }

  private phaseFromRadius(): number {
    const range = this.initialRadius - this.stormMinRadius;
    if (!Number.isFinite(range) || range <= 0) return 6;
    const progress = (this.initialRadius - this.currentRadius) / range;
    return Math.min(6, Math.max(1, Math.floor(progress * 6) + 1));
  }

  /** Pick the next zone center (small random drift) and radius for the current phase band. */
  private planNextZone(): void {
    const phase = this.phaseFromRadius();
    const range = this.initialRadius - this.stormMinRadius;
    const band = range / 6;
    const targetR = Math.max(this.stormMinRadius, this.initialRadius - phase * band);
    this.nextRadius = targetR * this.outsideEdgeFactor;

    const driftMax = this.currentRadius * 0.1 * (0.35 + phase * 0.1);
    const angle = Math.random() * Math.PI * 2;
    const dist = driftMax * (0.4 + Math.random() * 0.6);
    this.nextCenter.set(
      this.center.x + Math.cos(angle) * dist,
      this.center.y,
      this.center.z + Math.sin(angle) * dist,
    );
  }

  private syncRimGeometry(): void {
    const safeR = Math.max(0.5, this.getSafeZoneRadius());
    const rimInner = Math.max(0.1, safeR - 1.4);
    const rimOuter = safeR + 0.15;
    this.rimMesh.geometry.dispose();
    const rimGeo = new THREE.RingGeometry(rimInner, rimOuter, 96);
    const rimColors = new Float32Array(rimGeo.attributes.position.count * 3);
    const rimPos = rimGeo.attributes.position;
    for (let i = 0; i < rimPos.count; i++) {
      const x = rimPos.getX(i);
      const y = rimPos.getY(i);
      const t = (Math.atan2(y, x) / (Math.PI * 2) + 0.5) % 1;
      this.rimScratch.copy(this.rimCyan).lerp(this.rimPurple, t);
      rimColors[i * 3] = this.rimScratch.r;
      rimColors[i * 3 + 1] = this.rimScratch.g;
      rimColors[i * 3 + 2] = this.rimScratch.b;
    }
    rimGeo.setAttribute('color', new THREE.BufferAttribute(rimColors, 3));
    this.rimMesh.geometry = rimGeo;
    this.rimMesh.position.set(this.center.x, 0.12, this.center.z);
  }

  private syncWallGeometry(): void {
    const safeR = Math.max(0.5, this.getSafeZoneRadius());
    this.wallMesh.geometry.dispose();
    this.wallMesh.geometry = new THREE.CylinderGeometry(safeR, safeR, this.wallHeight, 96, 1, true);
    this.wallMesh.position.set(this.center.x, this.wallHeight * 0.5, this.center.z);
  }

  /** Advance storm shrink per second. Call from the game loop. */
  update(dt: number): void {
    const prevPhase = this.stormPhase;
    const prev = this.currentRadius;
    const next = shrinkStormRadius(
      this.currentRadius,
      this.shrinkPerSecond,
      this.stormMinRadius,
      dt,
    );

    if (Math.abs(next - prev) >= 1e-6) {
      this.currentRadius = next;
      const inner = Math.max(0.1, this.currentRadius - this.wallDepth * 0.35);
      const outer = this.currentRadius + this.wallDepth;

      this.mesh.geometry.dispose();
      this.mesh.geometry = new THREE.RingGeometry(inner, outer, 96);
      this.syncWallGeometry();
      this.syncRimGeometry();
    }

    const centerLerp = Math.min(1, dt * 0.55);
    this.center.lerp(this.nextCenter, centerLerp);
    this.mesh.position.set(this.center.x, 0.08, this.center.z);
    this.rimMesh.position.set(this.center.x, 0.12, this.center.z);
    this.wallMesh.position.set(this.center.x, this.wallHeight * 0.5, this.center.z);
    this.lightningLines.position.set(this.center.x, 0, this.center.z);

    this.pulseT += dt;
    const playerOutside = this.isOutside(this.trackedPx, this.trackedPz);
    const safeR = Math.max(0.5, this.getSafeZoneRadius());
    const edgeDist = Math.hypot(this.trackedPx - this.center.x, this.trackedPz - this.center.z);
    // Phase 30X-1: stronger edge telegraph as player approaches safe rim
    const edgeProximity = 1 - Math.min(1, Math.abs(edgeDist - safeR) / 12);
    const edgeDrama =
      Math.max(0, edgeProximity) *
      (playerOutside ? 0.16 : 0.08) *
      (0.85 + Math.sin(this.pulseT * 6.2) * 0.15);
    const latePhaseBoost = this.stormPhase >= 5 || this.isFinalCircle()
      ? 0.026 + Math.sin(this.pulseT * 3.4) * 0.016
      : 0;
    const pulse =
      0.26 +
      Math.sin(this.pulseT * 2.4) * 0.11 +
      Math.sin(this.pulseT * 5.1) * 0.05 +
      Math.sin(this.pulseT * 8.3) * 0.025;
    const outsideOpacityBoost = playerOutside ? 0.075 + Math.sin(this.pulseT * 4.6) * 0.045 : 0;
    const slowPulse = 0.035 + Math.sin(this.pulseT * 1.25) * 0.025;
    this.wallMesh.material.opacity = Math.min(0.58, pulse + slowPulse + outsideOpacityBoost + latePhaseBoost + edgeDrama);
    this.mesh.material.opacity = Math.min(
      0.56,
      0.36 + slowPulse + Math.sin(this.pulseT * 2.4) * 0.12 + Math.sin(this.pulseT * 6.2) * 0.04 + (playerOutside ? 0.08 : 0) + latePhaseBoost + edgeDrama * 0.85,
    );

    const rimPulse = 0.52 + Math.sin(this.pulseT * 3.2) * 0.18 + Math.sin(this.pulseT * 7.4) * 0.06;
    this.rimMesh.material.opacity = Math.min(0.9, rimPulse + (playerOutside ? 0.1 : 0) + latePhaseBoost * 0.8 + edgeDrama * 1.2);
    this.rimScratch.copy(this.rimCyan).lerp(this.rimPurple, 0.5 + Math.sin(this.pulseT * 2.1) * 0.5);
    if (this.colorblindMode) {
      this.rimScratch.setHex(0xffaa44);
    }

    this.lightningFlash = Math.max(0, this.lightningFlash - dt);
    this.skyLightningFlash = Math.max(0, this.skyLightningFlash - dt * 2.6);
    this.lightningCd -= dt;
    if (this.lightningCd <= 0) {
      this.lightningFlash = 0.09 + Math.random() * 0.05;
      this.lightningCd = 2.2 + Math.random() * 4.5;
      this.refreshLightningArcs();
    }
    const flash = this.lightningFlash > 0 ? Math.min(1, this.lightningFlash * 14) : 0;
    this.lightningLines.material.opacity = flash > 0.02 ? 0.18 + flash * 0.72 : 0;
    const outsideEmissive = playerOutside ? 0.62 + Math.sin(this.pulseT * 4.2) * 0.38 : 0;
    const edgeEmissive = edgeDrama * (1.35 + Math.sin(this.pulseT * 5.5) * 0.45);
    if (flash > 0.02) {
      this.wallMesh.material.emissive.setHex(0xffffff);
      this.wallMesh.material.emissiveIntensity = Math.min(1.35, Math.max(outsideEmissive + edgeEmissive, flash * 3.2));
    } else if (playerOutside || edgeDrama > 0.01) {
      this.wallMesh.material.emissive.setHex(this.colorblindMode ? 0xff8844 : 0xcc66ff);
      this.wallMesh.material.emissiveIntensity = Math.min(1.08, outsideEmissive + edgeEmissive);
    } else if (latePhaseBoost > 0) {
      this.wallMesh.material.emissive.setHex(this.colorblindMode ? 0xff8844 : 0xcc66ff);
      this.wallMesh.material.emissiveIntensity = Math.min(0.22, latePhaseBoost * 4);
    } else {
      this.wallMesh.material.emissive.setHex(0x000000);
      this.wallMesh.material.emissiveIntensity = 0;
    }
    this.wallMesh.material.color.copy(this.colorblindMode ? this.wallCbColor : this.wallBaseColor);
    if (flash > 0.02) {
      this.wallMesh.material.color.lerp(this.flashWhite, flash * 0.55);
    } else if (playerOutside) {
      this.wallMesh.material.color.lerp(this.colorblindMode ? this.outsideWallCbColor : this.outsideWallColor, 0.22);
    }

    this.stormPhase = this.phaseFromRadius();
    if (this.stormPhase !== prevPhase) {
      this.planNextZone();
      this.triggerPhaseAdvanceLightning();
    }

    this.finalCircleCrunchMult = this.isFinalCircle() ? 2 : 1;
  }

  private triggerPhaseAdvanceLightning(): void {
    this.lightningFlash = 0.24 + Math.random() * 0.1;
    this.skyLightningFlash = 1;
    this.lightningCd = 0.35 + Math.random() * 0.25;
    this.refreshLightningArcs();
  }

  private refreshLightningArcs(): void {
    const r = Math.max(2, this.currentRadius + this.wallDepth * 0.5);
    let i = 0;
    for (let arc = 0; arc < this.lightningArcCount; arc++) {
      const baseA = Math.random() * Math.PI * 2;
      const arcSpan = 0.08 + Math.random() * 0.16;
      let prevA = baseA;
      let prevY = 4 + Math.random() * (this.wallHeight - 8);
      let prevR = r + (Math.random() - 0.5) * this.wallDepth;
      for (let seg = 0; seg < this.lightningSegmentsPerArc; seg++) {
        const nextA = baseA + arcSpan * ((seg + 1) / this.lightningSegmentsPerArc);
        const nextY = Math.max(2, Math.min(this.wallHeight - 2, prevY + (Math.random() - 0.5) * 13));
        const nextR = r + (Math.random() - 0.5) * this.wallDepth;
        this.lightningPositions[i++] = Math.cos(prevA) * prevR;
        this.lightningPositions[i++] = prevY;
        this.lightningPositions[i++] = Math.sin(prevA) * prevR;
        this.lightningPositions[i++] = Math.cos(nextA) * nextR;
        this.lightningPositions[i++] = nextY;
        this.lightningPositions[i++] = Math.sin(nextA) * nextR;
        prevA = nextA;
        prevY = nextY;
        prevR = nextR;
      }
    }
    this.lightningPosAttr.needsUpdate = true;
  }

  /** Optional: cache player XZ before `getStormDamage(dt)` (avoids duplicate `isOutside` calls). */
  syncPlayer(px: number, pz: number): void {
    this.trackedPx = px;
    this.trackedPz = pz;
  }

  isOutside(px: number, pz: number): boolean {
    return isOutsideStorm(
      px,
      pz,
      this.center.x,
      this.center.z,
      this.currentRadius,
      this.outsideEdgeFactor,
    );
  }

  /** Damage for this tick while outside; uses last `syncPlayer(px,pz)`. */
  getStormDamage(dt: number): number {
    if (dt <= 0) return 0;
    if (!this.isOutside(this.trackedPx, this.trackedPz)) return 0;
    return computeStormTickDamage(
      dt,
      this.damagePerSecond,
      this.getEffectiveDamageMultiplier(),
    );
  }

  /** Push/pull scene fog toward a magenta storm tint while outside. */
  setOutsideTint(active: boolean): void {
    if (!this.fog || !this.fogBase) return;
    if (active === this.outsideTintActive) return;
    this.outsideTintActive = active;

    if (!active) {
      this.fog.color.copy(this.fogBase.color);
      this.fog.near = this.fogBase.near;
      this.fog.far = this.fogBase.far;
      return;
    }

    this.lerpScratch.copy(this.fogBase.color).lerp(this.tintColor, 0.28);
    this.fog.color.copy(this.lerpScratch);
    this.fog.near = this.fogBase.near * 0.97;
    this.fog.far = this.fogBase.far * 0.92;
  }

  dispose(): void {
    this.scene.remove(this.mesh);
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
    this.scene.remove(this.rimMesh);
    this.rimMesh.geometry.dispose();
    this.rimMesh.material.dispose();
    this.scene.remove(this.wallMesh);
    this.wallMesh.geometry.dispose();
    this.wallMesh.material.dispose();
    this.scene.remove(this.lightningLines);
    this.lightningLines.geometry.dispose();
    this.lightningLines.material.dispose();
    if (this.fog && this.fogBase && this.outsideTintActive) {
      this.fog.color.copy(this.fogBase.color);
      this.fog.near = this.fogBase.near;
      this.fog.far = this.fogBase.far;
    }
  }
}
